<template>
  <div class="max-w-5xl mx-auto px-6 py-12 text-gray-800">
    <h1 class="text-4xl font-bold mb-10 text-center text-primary">Conditions Générales d’Utilisation – Clients</h1>

    <!-- ARTICLE 1 -->
    <section class="mb-10">
      <h2 class="text-2xl font-semibold mb-4">1. Objet</h2>
      <p>
        Les présentes Conditions Générales d’Utilisation (ci-après « CGU ») ont pour objet de définir les modalités et conditions selon lesquelles les clients (utilisateurs particuliers) peuvent accéder et utiliser la plateforme BookMySalon (ci-après « la Plateforme »), éditée par [Nom de l’entreprise].
      </p>
    </section>

    <!-- ARTICLE 2 -->
    <section class="mb-10">
      <h2 class="text-2xl font-semibold mb-4">2. Définitions</h2>
      <ul class="list-disc pl-5 space-y-2">
        <li><strong>Client :</strong> toute personne physique utilisant la Plateforme pour réserver un service.</li>
        <li><strong>Professionnel :</strong> tout prestataire de services inscrit sur la Plateforme (salon, institut, indépendant).</li>
        <li><strong>Plateforme :</strong> le site web ou l’application mobile BookMySalon.</li>
      </ul>
    </section>

    <!-- ARTICLE 3 -->
    <section class="mb-10">
      <h2 class="text-2xl font-semibold mb-4">3. Inscription et compte utilisateur</h2>
      <p>
        L’accès aux fonctionnalités de réservation nécessite la création d’un compte client. Le client s’engage à fournir des informations exactes, complètes et à jour. BookMySalon se réserve le droit de suspendre un compte en cas de soupçon de fraude ou de comportement abusif.
      </p>
    </section>

    <!-- ARTICLE 4 -->
    <section class="mb-10">
      <h2 class="text-2xl font-semibold mb-4">4. Réservation de services</h2>
      <p>
        Le client peut réserver une ou plusieurs prestations auprès des professionnels disponibles. Un email ou SMS de confirmation est envoyé à la suite de la réservation. La prestation est fournie par le professionnel ; BookMySalon n’intervient qu’en tant qu’intermédiaire.
      </p>
    </section>

    <!-- ARTICLE 5 -->
    <section class="mb-10">
      <h2 class="text-2xl font-semibold mb-4">5. Annulation, modification et remboursement</h2>
      <ul class="list-disc pl-5 space-y-2">
        <li>Le client peut annuler un rendez-vous via la Plateforme selon les délais définis par chaque salon.</li>
        <li>Selon le moment de l’annulation, un remboursement total ou partiel pourra être effectué :</li>
        <ul class="list-[circle] pl-8 space-y-1">
          <li><strong>Plus de 24h avant :</strong> remboursement intégral possible (si acompte payé).</li>
          <li><strong>Moins de 24h avant :</strong> remboursement partiel ou aucun, selon la politique du professionnel.</li>
        </ul>
        <li>Les professionnels ont le droit d’exiger un <strong>acompte obligatoire</strong> pour certaines prestations ou pour certains clients.</li>
        <li>En cas de retard ou d'absence du client, le professionnel est libre de facturer tout ou partie du service.</li>
        <li>Le client accepte que les règles d’annulation et de remboursement soient fixées librement par chaque salon et visibles avant la validation du rendez-vous.</li>
      </ul>
    </section>

    <!-- ARTICLE 6 -->
    <section class="mb-10">
      <h2 class="text-2xl font-semibold mb-4">6. Acompte et politique du salon</h2>
      <p>
        Les professionnels peuvent activer la demande d’acompte pour :
      </p>
      <ul class="list-disc pl-5 space-y-2 mt-2">
        <li>toutes les prestations,</li>
        <li>ou uniquement certains clients (par exemple ceux ayant déjà manqué un rendez-vous sans prévenir).</li>
      </ul>
      <p class="mt-2">
        Le montant de l’acompte et ses conditions de remboursement sont précisés lors de la réservation. En cas d’annulation tardive ou de non-présentation, l’acompte pourra être conservé.
      </p>
    </section>

    <!-- ARTICLE 7 -->
    <section class="mb-10">
      <h2 class="text-2xl font-semibold mb-4">7. Blocage et exclusion de client</h2>
      <p>
        Un professionnel peut bloquer l’accès à son agenda pour un client spécifique en cas de :
      </p>
      <ul class="list-disc pl-5 space-y-2 mt-2">
        <li>comportement irrespectueux ou inapproprié,</li>
        <li>absences répétées sans annulation,</li>
        <li>tentatives de fraude ou de non-paiement.</li>
      </ul>
      <p class="mt-2">
        Ce blocage est propre au salon concerné et ne s’applique pas aux autres salons présents sur la Plateforme.
      </p>
    </section>

    <!-- ARTICLE 8 -->
    <section class="mb-10">
      <h2 class="text-2xl font-semibold mb-4">8. Données personnelles et confidentialité</h2>
      <p>
        Conformément au Règlement Général sur la Protection des Données (RGPD), BookMySalon collecte uniquement les données nécessaires à l’usage du service (nom, email, numéro, historique de rendez-vous…). Ces données ne sont jamais revendues.
      </p>
      <p class="mt-2">
        Le client peut exercer ses droits (accès, rectification, suppression) via :
        <a href="mailto:privacy@bookmysalon.fr" class="text-blue-600 underline">privacy@bookmysalon.fr</a>.
      </p>
    </section>

    <!-- ARTICLE 9 -->
    <section class="mb-10">
      <h2 class="text-2xl font-semibold mb-4">9. Suspension ou suppression de compte</h2>
      <p>
        En cas de non-respect des présentes CGU, BookMySalon se réserve le droit de suspendre ou supprimer un compte utilisateur, temporairement ou définitivement, notamment en cas de :
      </p>
      <ul class="list-disc pl-5 space-y-2 mt-2">
        <li>faux profils,</li>
        <li>propos injurieux ou comportements inappropriés,</li>
        <li>tentatives de contournement des règles de réservation ou de paiement.</li>
      </ul>
    </section>

    <!-- ARTICLE 10 -->
    <section class="mb-10">
      <h2 class="text-2xl font-semibold mb-4">10. Propriété intellectuelle</h2>
      <p>
        Tous les contenus (textes, images, logos, codes) de la Plateforme sont la propriété exclusive de BookMySalon ou de ses partenaires. Toute reproduction, diffusion ou exploitation est interdite sans autorisation écrite préalable.
      </p>
    </section>

    <!-- ARTICLE 11 -->
    <section class="mb-10">
      <h2 class="text-2xl font-semibold mb-4">11. Responsabilité</h2>
      <p>
        BookMySalon agit comme intermédiaire technique. Elle ne garantit pas la qualité, la conformité ou la disponibilité des prestations proposées par les professionnels, ni le respect des horaires convenus.
      </p>
      <p class="mt-2">
        En cas de litige entre client et professionnel, BookMySalon n’intervient qu’à titre de médiateur informel.
      </p>
    </section>

    <!-- ARTICLE 12 -->
    <section class="mb-10">
      <h2 class="text-2xl font-semibold mb-4">12. Modification des CGU</h2>
      <p>
        BookMySalon se réserve le droit de modifier les présentes CGU à tout moment. Les modifications prennent effet à leur publication sur la Plateforme. L’acceptation renouvelée pourra être demandée lors d’une connexion future.
      </p>
    </section>

    <!-- ARTICLE 13 -->
    <section class="mb-10">
      <h2 class="text-2xl font-semibold mb-4">13. Droit applicable et litiges</h2>
      <p>
        Les présentes CGU sont régies par le droit français. En cas de litige, une solution amiable sera d’abord recherchée. À défaut, les tribunaux français seront seuls compétents.
      </p>
    </section>

    <p class="text-sm text-gray-500 mt-10 text-center">
      Dernière mise à jour : 24 juin 2025
    </p>
  </div>
</template>

<script setup>
// Pas de logique JS nécessaire pour cette page statique
</script>
